import React from 'react';
import { X, Bell } from 'lucide-react';
import Button from '../ui/Button';
import { getRecoveryInstructions } from '../../lib/notificationPermission';

interface PermissionRecoveryModalProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Platform-specific instructions for re-enabling notifications when the
 * user has previously denied them. The browser API can't be re-prompted
 * programmatically once denied — the user must toggle the permission in
 * browser/OS settings. This modal walks them through it.
 *
 * The PermissionRecoveryBanner opens this modal. Once the user manually
 * re-enables the permission, the next app load triggers
 * `syncPermissionState` which detects the granted state, silently
 * re-subscribes, and shows a success toast — no further action needed
 * from this UI.
 */
const PermissionRecoveryModal: React.FC<PermissionRecoveryModalProps> = ({
    open,
    onClose,
}) => {
    if (!open) return null;
    const { title, steps } = getRecoveryInstructions();

    return (
        <div
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
            onClick={onClose}
        >
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                aria-hidden="true"
            />
            <div
                className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[92dvh] overflow-y-auto px-5 pt-5 pb-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="recovery-modal-title"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="!min-h-0 !min-w-0 absolute right-3 top-3 p-1 text-stone-400 hover:text-stone-600"
                    aria-label="Cerrar"
                >
                    <X size={18} />
                </button>

                <div className="flex items-start gap-3 mb-4 pr-6">
                    <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 shrink-0">
                        <Bell size={20} />
                    </div>
                    <div>
                        <h2
                            id="recovery-modal-title"
                            className="text-lg font-medium text-stone-900 leading-snug"
                        >
                            {title}
                        </h2>
                        <p className="text-sm text-stone-600 mt-1 leading-relaxed">
                            Cuando termines, vuelve a Ethernal. Los avisos
                            empezarán a llegar al instante.
                        </p>
                    </div>
                </div>

                <ol className="space-y-3 mb-6">
                    {steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full bg-rose-500 text-white text-xs font-medium flex items-center justify-center shrink-0 mt-0.5">
                                {i + 1}
                            </span>
                            <p className="text-sm text-stone-700 leading-relaxed">
                                {step}
                            </p>
                        </li>
                    ))}
                </ol>

                <Button onClick={onClose} fullWidth variant="danger">
                    Entendido
                </Button>
            </div>
        </div>
    );
};

export default PermissionRecoveryModal;
